import React, { Fragment } from 'react';
import { TextField, TextFieldMessage } from '@/components/upp';
import { Field } from '@atlaskit/form';
import { Button, ButtonAppearance } from '../Button';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { BREAKPOINTS } from '@constants/breakpoints';

interface NewsletterInputProps {
  isLoading: boolean;
  response: {
    message?: string;
    isError: boolean;
  };
}

/* 
    NOTE: 

    The responsive layout of the submit button is difficult due to the dynamic error /success
    state of the input and submission. If you keep once button you get a center alignment issue 
    by a pixel or so that does not look right. Have two buttons that hide and display on break 
    points solves this for now. 
*/

export const NewsletterInput: React.FC<NewsletterInputProps> = ({ response, isLoading }) => {
  const { message, isError: responseError } = response;

  return (
    <div css={inputStyles}>
      <Field name="email" label="Email address" defaultValue="">
        {({ fieldProps, error, meta }) => {
          const validationErrorMessage = meta.dirtySinceLastSubmit ? undefined : error;

          const displayMessage = validationErrorMessage || message;
          const displayType = error || responseError ? 'error' : 'valid';

          return (
            <Fragment>
              <div css={inputButtonFlex}>
                <TextField {...fieldProps} required type="email" autoComplete="off" placeholder="yourname@email.com" />
                <div css={buttonWrapper}>
                  <Button
                    as="button"
                    label="Sign up"
                    type="submit"
                    appearance={ButtonAppearance.PRIMARY}
                    isLoading={isLoading}
                  />
                </div>
              </div>
              {displayMessage ? <TextFieldMessage type={displayType} message={displayMessage} /> : null}
            </Fragment>
          );
        }}
      </Field>
    </div>
  );
};

const inputStyles = css({
  '& label': {
    fontWeight: 400,
  },
});

const inputButtonFlex = css({ display: 'flex', alignContent: 'center', gap: token('space.100') });

export const buttonWrapper = ({ isMobile }: { isMobile?: boolean }) =>
  css({
    minWidth: 'fit-content',
    display: isMobile ? 'none' : 'block', // Use props to conditionally apply styles
    marginTop: isMobile ? token('space.200') : 0,
    [BREAKPOINTS.MOBILE.MEDIA_QUERY]: {
      minWidth: '100%',
      display: isMobile ? 'block' : 'none',
    },
  });
